// ─── src/utils/weddingTiming.js ───────────────────────────────────────────────
// The Wedding's heartbeat: how far away is the day. Pure and honest, parseWeddingTiming
// returns a signal ONLY when the couple actually states a "when" (never guessed).
// The value isn't the exact date, it's the HORIZON, which shapes Aura's guidance:
// nine months out → venue first; six weeks out → work with what's available.

const MONTHS = ['january','february','march','april','may','june','july','august','september','october','november','december'];
const SEASON_MONTH = { spring: 3, summer: 6, autumn: 9, fall: 9, winter: 11 }; // 0-indexed target month
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Whole/fractional months from now to a target date (never negative).
function monthsBetween(now, target) {
  const m = (target.getFullYear() - now.getFullYear()) * 12 + (target.getMonth() - now.getMonth());
  const frac = (target.getDate() - now.getDate()) / 30;
  return Math.max(0, Math.round((m + frac) * 10) / 10);
}
// Next occurrence of a month (0-indexed); if no year given and it's already passed, roll to next year.
function nextMonth(now, monthIdx, year) {
  let y = year ?? now.getFullYear();
  if (year == null && monthIdx <= now.getMonth()) y += 1;
  return new Date(y, monthIdx, 15);
}

export function parseWeddingTiming(text, now = new Date()) {
  if (!text || typeof text !== 'string') return null;
  const t = ` ${text.toLowerCase()} `;

  // Priority: explicit month+year → next month → qualified season → relative → next year.
  const monthYear = t.match(new RegExp(`\\b(${MONTHS.join('|')})\\s+(\\d{4})\\b`));
  if (monthYear) {
    const i = MONTHS.indexOf(monthYear[1]); const year = Number(monthYear[2]);
    const target = new Date(year, i, 15);
    return { date: iso(target), label: `${cap(monthYear[1])} ${year}`, monthsOut: monthsBetween(now, target) };
  }
  // "next June", "in June", "by December", a month with a timing qualifier (avoids "you may").
  const nextMo = t.match(new RegExp(`\\b(?:in|next|by|around|on)\\s+(${MONTHS.join('|')})\\b`));
  if (nextMo) {
    const i = MONTHS.indexOf(nextMo[1]); const target = nextMonth(now, i, null);
    return { date: iso(target), label: cap(nextMo[1]), monthsOut: monthsBetween(now, target) };
  }
  // Seasons, require a qualifier ("next summer", "in the summer") or a year ("summer 2027"),
  // so "fall in love" and "a summer garden" never trigger.
  const qSeason = t.match(/\b(?:next|this|in(?: the)?|by|around)\s+(spring|summer|autumn|fall|winter)\b\s*(\d{4})?/);
  const ySeason = t.match(/\b(spring|summer|autumn|winter)\s+(\d{4})\b/);
  const season = qSeason || ySeason;
  if (season) {
    const name = season[1]; const year = season[2] ? Number(season[2]) : null;
    const target = nextMonth(now, SEASON_MONTH[name], year);
    return { date: null, label: `${cap(name)}${year ? ' ' + year : ''}`, monthsOut: monthsBetween(now, target) };
  }
  let m;
  if ((m = t.match(/\b(\d+)\s*weeks?\b/)))  { const w = Number(m[1]); return { date: null, label: `In ${w} week${w > 1 ? 's' : ''}`,  monthsOut: Math.round((w / 4.345) * 10) / 10 }; }
  if ((m = t.match(/\b(\d+)\s*months?\b/))) { const o = Number(m[1]); return { date: null, label: `In ${o} month${o > 1 ? 's' : ''}`, monthsOut: o }; }
  if ((m = t.match(/\b(\d+)\s*years?\b/)))  { const y = Number(m[1]); return { date: null, label: `In ${y} year${y > 1 ? 's' : ''}`,  monthsOut: y * 12 }; }
  if (/\bnext year\b/.test(t)) return { date: null, label: 'Next year', monthsOut: 12 };
  return null;
}

// A season / relative expression has no exact date, but we still want a horizon that
// stays accurate as time passes, so we store an APPROXIMATE date (mid-month) and
// recompute months-until from it on every read.
export function approxDateFromMonths(monthsOut, now = new Date()) {
  if (monthsOut == null) return null;
  const d = new Date(now.getFullYear(), now.getMonth(), 15);
  d.setMonth(d.getMonth() + Math.round(monthsOut));
  return iso(d);
}

export function monthsUntil(dateStr, now = new Date()) {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d)) return null;
  return monthsBetween(now, d);
}

// The behaviour lever. How far away decides what Aura does next.
export function timeHorizon(monthsOut) {
  if (monthsOut == null) return null;
  if (monthsOut < 2)  return 'imminent';
  if (monthsOut < 5)  return 'soon';
  if (monthsOut < 15) return 'comfortable';
  return 'distant';
}

// Couple-facing "how far away" for the wedding rail. Prefers the couple's OWN words
// (their label, the most honest phrasing), else a live countdown from the stored date.
export function describeTiming(wedding, now = new Date()) {
  if (!wedding) return null;
  if (wedding.date_label) return wedding.date_label;
  const mo = wedding.wedding_date ? monthsUntil(wedding.wedding_date, now) : null;
  if (mo == null) return null;
  if (mo < 1)  return 'weeks away';
  if (mo < 2)  return 'about a month away';
  if (mo < 22) return `about ${Math.round(mo)} months away`;
  const y = Math.round(mo / 12); return `${y} year${y > 1 ? 's' : ''} away`;
}
